import { parseDbTimestamp } from "../db-timestamp";
import type { FollowedFlowTopics, OpenTopic } from "./flow-open-topics";

/**
 * A client meeting. Its timestamps (`occurredAt`, `recordingConsentAt`,
 * `recordingFinishedAt`, `createdAt`, `updatedAt`) are the database's zone-less UTC form,
 * e.g. `"2026-10-23 10:43:45.224"` — not ISO 8601. Read them as UTC
 * (`parseDbTimestamp`); `Instant.parse()` refuses them.
 */
export type ClientMeeting = {
  id: string;
  clientId: string;
  occurredAt: string;
  topic: string;
  content: string;
  summary: string | null;
  summaryStale: boolean;
  hasMeetingRoom: boolean;
  roomDurationMinutes: number;
  roomUrl: string | null;
  hostRoomUrl: string | null;
  wherebyMeetingId: string | null;
  wherebyRecordingId: string | null;
  wherebyTranscriptionId: string | null;
  transcription: string | null;
  transcriptionStatus: string | null;
  transcriptionLanguage: string | null;
  /**
   * QL-727 — when the advisor confirmed the client knows the meeting is transcribed (zone-less UTC).
   * A meeting with a Whereby transcript (`transcription` set, or `transcriptionStatus`
   * "in_progress") cannot be recorded on the phone: consent and chunks answer 409
   * `meeting_has_transcription`.
   */
  recordingConsentAt: string | null;
  /**
   * QL-727 — when the phone recording was last finished (`POST …/finish`, zone-less UTC).
   * A finished meeting is left out of `GET /internal/client-meetings/upcoming`.
   */
  recordingFinishedAt: string | null;
  createdById: string;
  /** `deletedAt` is set when the user was deleted (QL-365); the activity feed marks the name. */
  createdBy?: { id: string; name: string; deletedAt?: string | null };
  updatedById: string;
  updatedBy?: { id: string; name: string; deletedAt?: string | null };
  createdAt: string;
  updatedAt: string;
};

export type CreateClientMeetingRequest = {
  topic: string;
  content?: string;
  occurredAt?: string;
  hasMeetingRoom?: boolean;
  roomDurationMinutes?: number;
};

export type UpdateClientMeetingRequest = {
  topic?: string;
  content?: string;
  occurredAt?: string;
  hasMeetingRoom?: boolean;
  roomDurationMinutes?: number;
};

/**
 * QL-727 — the multipart fields of
 * POST /internal/client-meetings/:clientId/:meetingId/transcript-chunks.
 * Every value is a form string; `audio` is the file part. A malformed field is
 * a 400 before the meeting, toggle and consent checks.
 */
export interface TranscriptChunkFormFields {
  /** The audio chunk: `audio/mp4` (m4a) or `audio/aac` (a `.m4a` / `.aac` file name also works untyped), max 25 MB. Never stored. */
  audio: File | Blob;
  /**
   * Integer ≥ 0, monotonic per MEETING across every recording session: a resumed
   * or second session continues at the highest sequence sent + 1. A sequence
   * already stored returns the stored chunk unchanged (the new audio is ignored),
   * so restarting at 0 would drop the new speech.
   */
  sequence: string;
  /** Integer ≥ 0: the chunk's start, in ms from the start of the recording. */
  startedAtMs: string;
  /** Optional 2-letter Whisper code ("no", "sv", "en", …); anything else auto-detects. */
  language?: string;
  /**
   * Optional: the client's flow instance to follow. Nothing about it is stored.
   * A flow that cannot be read never fails the upload: see {@link FollowedFlowTopics}.
   */
  followFlowInstanceId?: string;
  /** Optional JSON array of topic keys the phone already shows as covered (never un-ticked). */
  coveredTopicKeys?: string;
}

/**
 * QL-727 — the answer to a chunk upload (fields: {@link TranscriptChunkFormFields}).
 * The audio is never stored.
 */
export interface TranscriptChunkResponse {
  sequence: number;
  /** This chunk's text. */
  text: string;
  /** The meeting's whole transcript so far: every chunk's text in sequence order. */
  transcription: string;
  /** Only when `followFlowInstanceId` was sent. */
  topics: FollowedFlowTopics | null;
}

/** QL-727 — one of the signed-in advisor's upcoming meetings. */
export interface UpcomingMeeting {
  id: string;
  clientId: string;
  clientName: string;
  topic: string | null;
  /** ISO 8601. */
  occurredAt: string;
  hasMeetingRoom: boolean;
}

/**
 * QL-727 — GET /internal/client-meetings/upcoming?days=14: meetings on the
 * caller's own clients from 2 hours ago (a meeting just started stays listed)
 * up to `days` (1–90, default 14) ahead, soonest first, at most 50. A meeting
 * whose recording was finished (`recordingFinishedAt`) is left out.
 */
export interface UpcomingMeetingsResponse {
  meetings: UpcomingMeeting[];
}

/** QL-727 — GET /internal/client-meetings/:clientId/:meetingId/prep. Deterministic (no AI). */
export interface MeetingPrepResponse {
  meeting: { id: string; clientId: string; topic: string | null; occurredAt: string };
  client: { id: string; displayName: string; email: string | null; phone: string | null };
  /** The last 3 meetings before this one, newest first. */
  recentMeetings: {
    id: string;
    topic: string | null;
    occurredAt: string;
    summary: string | null;
  }[];
  /** The client's flows still being worked on (draft / in progress). */
  openFlows: {
    flowInstanceId: string;
    flowName: string;
    status: string;
    percent: number;
    openTopics: OpenTopic[];
  }[];
  /** The 5 newest client files. */
  recentFiles: {
    id: string;
    fileName: string;
    mimeType: string;
    createdAt: string;
    summary: string | null;
    processingStatus: string;
  }[];
}

export type SyncTranscriptionResponse = {
  status: "ready" | "started" | "in_progress" | "no_recording" | "no_room" | "no_speech" | "error";
  message: string;
};

/**
 * QL-685 — true while a meeting has not started: `occurredAt` is after `now`.
 * `occurredAt` is either Postgres' zone-less UTC text form ("2026-10-17 14:00:00")
 * or an ISO instant with a zone, so it is parsed with parseDbTimestamp, never
 * compared as a string (" " sorts before "T", which hid every same-day meeting)
 * and never handed to `new Date()` (which reads the zone-less form as local time).
 * Unparseable values are "not upcoming" (same as the old NaN compare).
 */
export function isUpcomingMeeting(occurredAt: string, now: Date = new Date()): boolean {
  const at = parseDbTimestamp(occurredAt);
  return at !== null && at.getTime() > now.getTime();
}
